import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { useApi } from '../../../../hooks'
import { GretaAIChatStatus } from '../../GretaAIChat.constants'
import {
  createMockGretaAIChatProject,
  deleteMockGretaAIChatProject,
  getMockGretaAIChatProjects,
  renameMockGretaAIChatProject,
} from '../../GretaAIChat.mocks'
import { getGretaAIChatErrorStatus } from '../../GretaAIChat.utils'
import { getGretaAIChatProjectChatIds } from '../../GretaAIChat.utils.projects'

import {
  getGretaAIChatProjectsWithChats,
  mapGretaAIChatProject,
  mapGretaAIChatProjects,
  removeGretaAIChatProject,
  renameGretaAIChatProject,
} from './useGretaAIChatProjects.utils'

export function useGretaAIChatProjects({
  chats = EMPTY_LIST,
  isOpen = false,
} = EMPTY_OBJECT) {
  const [projects, setProjects] = useState(EMPTY_LIST)
  const [projectsStatus, setProjectsStatus] = useState(
    GretaAIChatStatus.Loading
  )

  const hasLoadedRef = useRef(false)

  const projectsApi = useApi()

  // TODO: read the projects from the projects endpoint once the backend serves it (IU-1835).
  async function fetchProjects({ options }) {
    setProjectsStatus(GretaAIChatStatus.Loading)

    try {
      const response = await getMockGretaAIChatProjects({ options })

      if (response) {
        setProjects(mapGretaAIChatProjects(response))
        setProjectsStatus(GretaAIChatStatus.Idle)
      }
    } catch (reason) {
      const errorStatus = await getGretaAIChatErrorStatus(reason)

      if (!options?.signal?.aborted) {
        setProjectsStatus(errorStatus)
      }
    }
  }

  function loadProjects() {
    projectsApi.call(fetchProjects)
  }

  // TODO: call the projects endpoint once the backend serves it (IU-1835).
  async function createProject(name) {
    const { data } = await createMockGretaAIChatProject({ name })

    setProjects(current => [mapGretaAIChatProject(data), ...current])
  }

  async function renameProject(id, name) {
    await renameMockGretaAIChatProject({ id, name })

    setProjects(current => renameGretaAIChatProject(current, { id, name }))
  }

  async function deleteProject(id) {
    await deleteMockGretaAIChatProject({ id })

    setProjects(current => removeGretaAIChatProject(current, id))
  }

  useEffect(() => {
    if (isOpen && !hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadProjects()
    }
  }, [isOpen])

  return {
    createProject,
    deleteProject,
    loadProjects,
    projectChatIds: getGretaAIChatProjectChatIds(projects),
    projects: getGretaAIChatProjectsWithChats(projects, chats),
    projectsStatus,
    renameProject,
  }
}
